import { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { FiX } from 'react-icons/fi';
import {
  partitionBacklogDisplay,
  epicBacklogSummary,
  storyBacklogSummary,
} from '../utils/backlogDisplay';
import { colors } from '../theme/colors';

const PRIORITY_META = {
  Blocker: { color: colors.priority.blocker.text, bg: colors.priority.blocker.bg },
  Critical: { color: colors.priority.critical.text, bg: colors.priority.critical.bg },
  High: { color: colors.priority.high.text, bg: colors.priority.high.bg },
  Medium: { color: colors.priority.medium.text, bg: colors.priority.medium.bg },
  Low: { color: colors.priority.low.text, bg: colors.priority.low.bg },
};

function BacklogItemCard({
  issue,
  summaryLine,
  sprintList,
  onMoveToSprint,
  onOpen,
}) {
  const p = PRIORITY_META[issue.priority] || PRIORITY_META.Low;
  const [showMove, setShowMove] = useState(false);
  const [targetSprint, setTargetSprint] = useState('');
  const title =
    issue.description?.split('\n')[0]?.trim() || issue.description || '(No title)';
  const isEpic = issue.issuetype === 'Epic';

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        borderLeft: `4px solid ${isEpic ? '#4f46e5' : '#7c3aed'}`,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 120,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
            #{issue.issueid} · {issue.issuetype}
            {isEpic && issue.epic_status ? ` · ${issue.epic_status}` : ''}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: '#1e293b', mt: 0.25, lineHeight: 1.35 }}
            noWrap
            title={title}
          >
            {title}
          </Typography>
          {summaryLine ? (
            <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, display: 'block', mt: 0.5 }}>
              {summaryLine}
            </Typography>
          ) : null}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            px: 0.75,
            py: 0.25,
            borderRadius: 10,
            background: p.bg,
            color: p.color,
            flexShrink: 0,
          }}
        >
          {issue.priority || 'Low'}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => onOpen?.(issue)}
          style={{
            flex: 1,
            minWidth: 100,
            padding: '6px 10px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: '#f8fafc',
            fontWeight: 600,
            fontSize: '0.78rem',
            cursor: 'pointer',
            color: '#334155',
          }}
        >
          Open
        </button>
        {showMove ? (
          <Box sx={{ display: 'flex', gap: 0.5, width: '100%', flexWrap: 'wrap' }}>
            <select
              value={targetSprint}
              onChange={(e) => setTargetSprint(e.target.value)}
              style={{
                flex: 1,
                minWidth: 140,
                fontSize: '0.78rem',
                padding: '6px 8px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}
            >
              <option value="">Pick sprint…</option>
              {(sprintList || [])
                .filter((s) => !['Released', 'Closed'].includes(s.status))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.sprint_name} ({s.status})
                  </option>
                ))}
            </select>
            <button
              type="button"
              disabled={!targetSprint}
              onClick={() => {
                if (!targetSprint) return;
                onMoveToSprint(issue.issueid, targetSprint);
                setShowMove(false);
              }}
              style={{
                padding: '6px 12px',
                background: '#1272B3',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: targetSprint ? 'pointer' : 'not-allowed',
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowMove(false)}
              style={{
                padding: '6px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </Box>
        ) : (
          <button
            type="button"
            onClick={() => setShowMove(true)}
            disabled={!sprintList?.length}
            style={{
              flex: 1,
              minWidth: 100,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px dashed #7CCAF0',
              background: '#EAF6FD',
              fontWeight: 700,
              fontSize: '0.78rem',
              cursor: sprintList?.length ? 'pointer' : 'not-allowed',
              color: '#2C63AE',
            }}
          >
            + Add to Sprint
          </button>
        )}
      </Box>
    </Box>
  );
}

export default function BacklogDialog({
  open,
  onClose,
  projectName,
  backlogPayload,
  isLoading,
  sprintList,
  onMoveToSprint,
  onOpenEpic,
  onOpenStory,
  onAddStory,
}) {
  const allIssues = backlogPayload?.allIssues || [];
  const meta = backlogPayload?.meta || {};
  const { epics, stories } = useMemo(
    () => partitionBacklogDisplay(allIssues),
    [allIssues],
  );

  const displayCount = meta.displayCount ?? epics.length + stories.length;
  const hiddenCount = meta.hiddenCount ?? 0;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative', background: 'linear-gradient(90deg, #2C63AE, #1272B3)' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close backlog">
            <FiX size={22} />
          </IconButton>
          <Typography sx={{ ml: 1, flex: 1, fontWeight: 800 }} variant="h6" component="div">
            Backlog
            {projectName ? ` · ${projectName}` : ''}
          </Typography>
          {onAddStory ? (
            <button
              type="button"
              onClick={onAddStory}
              style={{
                marginRight: 8,
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#F1A028',
                color: '#2C63AE',
                fontWeight: 800,
                fontSize: '0.82rem',
                cursor: 'pointer',
              }}
            >
              + Add Story
            </button>
          ) : null}
        </Toolbar>
      </AppBar>
      <Box sx={{ p: { xs: 2, md: 3 }, background: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2, maxWidth: 720, lineHeight: 1.5 }}>
          Showing <strong>{displayCount}</strong> epic{epics.length === 1 ? '' : 's'} and{' '}
          <strong>{stories.length}</strong> stor{stories.length === 1 ? 'y' : 'ies'}.
          {hiddenCount > 0 ? (
            <>
              {' '}
              <strong>{hiddenCount}</strong> nested task{hiddenCount === 1 ? '' : 's'} stay with their parent
              (tasks cannot be moved to backlog on their own).
            </>
          ) : (
            ' Tasks stay under their story or epic.'
          )}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : displayCount === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: '#94a3b8',
              background: '#fff',
              borderRadius: 2,
              border: '1px dashed #e2e8f0',
            }}
          >
            No epics or stories in the backlog. Move an epic or story from a sprint, or add a story here.
          </Box>
        ) : (
          <>
            {epics.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', mb: 1.5 }}>
                  Epics ({epics.length})
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 1.5,
                  }}
                >
                  {epics.map((epic) => {
                    const { stories: sc, tasks: tc } = epicBacklogSummary(epic, allIssues);
                    const summaryLine =
                      sc || tc
                        ? `${sc} stor${sc === 1 ? 'y' : 'ies'}, ${tc} task${tc === 1 ? '' : 's'} included`
                        : 'No stories yet';
                    return (
                      <BacklogItemCard
                        key={epic.issueid}
                        issue={epic}
                        summaryLine={summaryLine}
                        sprintList={sprintList}
                        onMoveToSprint={onMoveToSprint}
                        onOpen={() => onOpenEpic?.(epic)}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
            {stories.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', mb: 1.5 }}>
                  Stories ({stories.length})
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 1.5,
                  }}
                >
                  {stories.map((story) => {
                    const { tasks: tc } = storyBacklogSummary(story, allIssues);
                    const summaryLine =
                      tc > 0 ? `${tc} task${tc === 1 ? '' : 's'} included` : 'No tasks yet';
                    return (
                      <BacklogItemCard
                        key={story.issueid}
                        issue={story}
                        summaryLine={summaryLine}
                        sprintList={sprintList}
                        onMoveToSprint={onMoveToSprint}
                        onOpen={() => onOpenStory?.(story)}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
}
